import { MapPin, LifeBuoy, Briefcase, ArrowUpRight } from "lucide-react";
import Brand from "@/components/site/Brand";
import BrandName from "@/components/site/BrandName";

const platformLinks = [
  ["Overview", "#process"],
  ["Try the assessment", "#tryit"],
  ["Pricing", "#pricing"],
  ["Trust & security", "#features"],
  ["FAQ", "#faq"],
];

const companyLinks = [
  ["About", "#about"],
  ["Contact", "#contact"],
  ["Sign in", "/app/login"],
];

const legalLinks = [
  ["Privacy Policy", "/legal/privacy"],
  ["Terms of Service", "/legal/terms"],
  ["Cookie Policy", "/legal/cookies"],
];

const channels = [
  {
    icon: Briefcase,
    heading: "Sales",
    value: "sales@aidoido.co.uk",
    caption: "New business enquiries",
    href: "mailto:sales@aidoido.co.uk",
    testid: "footer-sales-email",
  },
  {
    icon: LifeBuoy,
    heading: "Support",
    value: "support@ukcsl.co.uk",
    caption: "For existing customers",
    href: "mailto:support@ukcsl.co.uk",
    testid: "footer-support-email",
  },
];

const locations = [
  { flag: "🇬🇧", label: "United Kingdom", detail: "Remote Sales & Support" },
  { flag: "🇪🇸", label: "Orihuela Costa, Alicante", detail: "European Operations" },
];

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-white/[0.06] pt-16 pb-8 bg-[#040914]"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        {/* Row 1 — Brand + Contact channels + Locations, all on one row on desktop */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 pb-12 border-b border-white/[0.06]">
          {/* Brand block */}
          <div className="lg:col-span-4">
            <Brand />
            <p className="mt-5 text-[13.5px] text-slate-400 leading-relaxed max-w-sm">
              Workforce assurance for regulated organisations. Every
              acknowledgement signed, every certificate verifiable, every event
              retained.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {["GDPR", "ISO 27001 aware", "UK Hosted", "SHA-256"].map((b) => (
                <span
                  key={b}
                  className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-md border border-white/10 text-slate-400"
                >
                  {b}
                </span>
              ))}
            </div>
          </div>

          {/* Channels */}
          <div className="lg:col-span-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4A65A] font-semibold mb-4">
              Contact
            </div>
            <div className="space-y-2.5">
              {channels.map((c) => {
                const Icon = c.icon;
                return (
                  <a
                    key={c.heading}
                    href={c.href}
                    data-testid={c.testid}
                    className="group flex items-center gap-3 rounded-lg border border-white/[0.08] hover:border-[#D4A65A]/40 bg-white/[0.02] px-4 py-3 transition-colors"
                  >
                    <span className="h-8 w-8 rounded-md bg-[#D4A65A]/10 border border-[#D4A65A]/25 flex items-center justify-center shrink-0">
                      <Icon size={13} className="text-[#D4A65A]" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[9.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                        {c.heading}
                      </div>
                      <div className="mt-0.5 text-white text-[13.5px] font-semibold tracking-tight break-all leading-snug">
                        {c.value}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>

          {/* Locations */}
          <div className="lg:col-span-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4A65A] font-semibold mb-4">
              Locations
            </div>
            <div className="space-y-2.5">
              {locations.map((l) => (
                <div
                  key={l.label}
                  data-testid={`footer-location-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="flex items-center gap-3 rounded-lg border border-white/[0.08] bg-white/[0.02] px-4 py-3"
                >
                  <span className="text-2xl leading-none shrink-0" aria-hidden="true">
                    {l.flag}
                  </span>
                  <div className="min-w-0">
                    <div className="text-white text-[13.5px] font-semibold tracking-tight leading-snug">
                      {l.label}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1 text-[10.5px] text-slate-400 leading-snug">
                      <MapPin size={9} className="text-slate-500 shrink-0" />
                      {l.detail}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 2 — Link columns + Built-by side by side on desktop */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 py-10 border-b border-white/[0.06]">
          <div className="lg:col-span-4 grid grid-cols-3 gap-6">
            <LinkColumn title="Platform" links={platformLinks} testid="footer-col-platform" />
            <LinkColumn title="Company" links={companyLinks} testid="footer-col-company" />
            <LinkColumn title="Legal" links={legalLinks} testid="footer-col-legal" />
          </div>

          <div
            data-testid="footer-built-by"
            className="lg:col-span-8 rounded-xl border border-white/[0.08] bg-gradient-to-r from-[#0B1424] via-[#080F1E] to-[#0B1424] p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
          >
            <div className="shrink-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4A65A] font-semibold">
                Built by
              </div>
              <div className="mt-1 text-white text-[16px] font-semibold tracking-tight">
                Aidoido
              </div>
            </div>
            <p className="md:flex-1 text-[12.5px] text-slate-400 leading-relaxed">
              AI powered digital growth and product engineering for regulated
              markets. <BrandName /> is a UKCSL and Aidoido collaboration.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <a
                href="https://www.aidoido.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-aidoido-link"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-300 hover:text-white border border-white/10 hover:border-white/25 rounded-md px-3 py-1.5 transition-colors"
              >
                Aidoido <ArrowUpRight size={11} />
              </a>
              <a
                href="https://www.ukcsl.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-ukcsl-link"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-300 hover:text-white border border-white/10 hover:border-white/25 rounded-md px-3 py-1.5 transition-colors"
              >
                UKCSL <ArrowUpRight size={11} />
              </a>
            </div>
          </div>
        </div>

        {/* Row 3 — Copyright bar */}
        <div className="pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[11.5px] text-slate-500">
          <div data-testid="footer-copyright">
            © {year} <BrandName />. All rights reserved. Registered in the
            United Kingdom.
          </div>
          <div className="flex items-center gap-6">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              All systems operational
            </span>
            <span className="font-mono">v2.7 · Feb 2026</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function LinkColumn({ title, links, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4A65A] font-semibold">
        {title}
      </div>
      <ul className="mt-4 space-y-2.5">
        {links.map(([label, href]) => (
          <li key={label}>
            <a
              href={href}
              data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="text-[13px] text-slate-400 hover:text-white transition-colors"
            >
              {label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
